import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/contexts/AuthContext';
import { marketingKeys } from '@/hooks/queries/useMarketingQueries';

const CRM_INVALIDATE_DEBOUNCE_MS = 750;

/**
 * Real-time Sales CRM refresh — listens for `websocket:crm_update` (single-contact
 * edits, registration links, manual notes) and debounces React Query invalidation so
 * burst events collapse to one refetch wave.
 */
export const useRealTimeCrmUpdates = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.user_role === 'admin' || user?.role === 'admin';
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    if (!isAdmin) return undefined;

    const invalidateCrmQueries = () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.actionCenter() });
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === marketingKeys.all[0] &&
          query.queryKey[1] === marketingKeys.all[1] &&
          ['contacts', 'contact', 'contact-history', 'contact-timeline', 'contact-follow-ups'].includes(
            query.queryKey[2]
          ),
      });
    };

    const handleCrmUpdate = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        invalidateCrmQueries();
      }, CRM_INVALIDATE_DEBOUNCE_MS);
    };

    document.addEventListener('websocket:crm_update', handleCrmUpdate);
    return () => {
      document.removeEventListener('websocket:crm_update', handleCrmUpdate);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [isAdmin, queryClient]);
};

export default useRealTimeCrmUpdates;
